import { derived, type Readable } from 'svelte/store';
import { BaseViewModel, type BaseViewModelInterface } from '$services';
import type { ProjectData } from '@localekey/types';
import type { MenuOption } from '@shared/svelte';

export type ProjectActionsModelInterface = {
	readonly moreOptions: Readable<MenuOption[]>;

	download(): Promise<void>;
	translate(): Promise<void>;
	upload(): void;
} & BaseViewModelInterface;

class ProjectActionsModel
	extends BaseViewModel
	implements ProjectActionsModelInterface
{
	get moreOptions(): Readable<MenuOption[]> {
		return derived([this.t], ([$t]) => {
			const options: MenuOption[] = [
				{
					click: () => this.edit(),
					icon: 'pencil',
					text: $t.edit(),
				},
				{
					click: () => this.upload(),
					icon: 'upload',
					text: $t.upload(),
				},
				{
					click: () => this.download(),
					icon: 'download',
					iconColor: 'neutral-700',
					text: $t.download(),
				},
			];
			return options;
		});
	}
	async download(): Promise<void> {
		const project = await this._getCurrentProject();
		await this.execute('localeService', 'downloadAllLocales', project);
	}
	async upload(): Promise<void> {
		await this.execute('appService', 'setShowLocaleUploadDialog', true);
	}

	async translate(): Promise<void> {
		await this.execute('appService', 'setShowTranslateDialog', true);
	}

	async edit(): Promise<void> {
		const project = await this._getCurrentProject();
		await this.execute('projectService', 'setEditingProject', project);
		await this.execute('appService', 'setShowProjectFormDialog', true);
	}
	private async _getCurrentProject(): Promise<ProjectData> {
		const project = await this.get$('projectService', 'currentProject');
		if (!project) {
			throw new Error('No current project');
		}
		return project;
	}
}

export const getProjectActionsModel = (): ProjectActionsModelInterface =>
	new ProjectActionsModel({
		className: 'ProjectActionsModel',
	});
